// components/customers/CustomersTable.jsx
import React, { useState, useEffect } from 'react'
import { Phone, Mail, Bell, AlertCircle } from 'lucide-react'
import { apiFetch } from '../../api.js'
import { apiGetCustomerOrders } from './customersApi.js'

function cx(...classes) {
  return classes.filter(Boolean).join(' ')
}

function Pill({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    yellow: 'bg-amber-50 text-amber-700 ring-amber-100',
    blue: 'bg-sky-50 text-sky-700 ring-sky-100',
    red: 'bg-rose-50 text-rose-700 ring-rose-100',
    purple: 'bg-violet-50 text-violet-700 ring-violet-100',
    indigo: 'bg-slate-50 text-slate-800 ring-slate-200',
    orange: 'bg-amber-50 text-amber-800 ring-amber-100',
  }
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1',
        tones[tone] || tones.neutral
      )}
    >
      {children}
    </span>
  )
}

const RFM_SEG = {
  champion:   { label: 'Champion',  emoji: '🥇', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
  loyal:      { label: 'Fidèle',    emoji: '🥈', cls: 'bg-blue-50 text-blue-700 ring-blue-100'          },
  new:        { label: 'Nouveau',   emoji: '🌱', cls: 'bg-violet-50 text-violet-700 ring-violet-100'    },
  at_risk:    { label: 'À risque',  emoji: '⚠️', cls: 'bg-amber-50 text-amber-700 ring-amber-100'       },
  occasional: { label: 'Occasion.', emoji: '🔄', cls: 'bg-slate-100 text-slate-700 ring-slate-200'      },
  lost:       { label: 'Perdu',     emoji: '💤', cls: 'bg-rose-50 text-rose-700 ring-rose-100'          },
}

export function CustomersTable({
  customers,
  openId,
  setOpenId,
  onCustomerUpdated,
  saving,
  setSaving,
  dueTone,
  rfmMap = {},
}) {
  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState({})
  const [orders, setOrders] = useState([])
  const [loadingOrders, setLoadingOrders] = useState(false)
  const [saveErr, setSaveErr] = useState('')

  const inputBase =
    'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-300 focus:ring-2 focus:ring-slate-100'
  const inputError =
    'w-full rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100'

  const openCustomer = customers.find(c => c.uid === openId)

  useEffect(() => {
    if (!openId || !openCustomer) {
      setEditMode(false)
      setForm({})
      setOrders([])
      setSaveErr('')
      return
    }

    setForm({
      name: openCustomer.name || '',
      phone: openCustomer.phone || '',
      email: openCustomer.email || '',
      type: openCustomer.type || 'particulier',
      is_active: !!openCustomer.is_active,
    })

    // Load orders
    async function loadOrders() {
      setLoadingOrders(true)
      try {
        const data = await apiGetCustomerOrders(openCustomer.uid)
        setOrders(data.orders || [])
      } catch (e) {
        setOrders([])
      } finally {
        setLoadingOrders(false)
      }
    }
    loadOrders()
  }, [openId, openCustomer])

  const onChange = (k) => (e) => {
    const v = k === 'is_active' ? e.target.checked : e.target.value
    setForm((prev) => ({ ...prev, [k]: v }))
  }

  const buildPatch = () => {
    if (!openCustomer) return {}
    const patch = {}
    const trimOrNull = (v) => {
      const s = String(v ?? '').trim()
      return s === '' ? null : s
    }

    const nextName = trimOrNull(form.name)
    const nextPhone = trimOrNull(form.phone)
    const nextEmail = trimOrNull(form.email)
    const nextType = trimOrNull(form.type) || 'particulier'
    const nextActive = !!form.is_active

    const curName = trimOrNull(openCustomer.name)
    const curPhone = trimOrNull(openCustomer.phone)
    const curEmail = trimOrNull(openCustomer.email)
    const curType = trimOrNull(openCustomer.type) || 'particulier'
    const curActive = !!openCustomer.is_active

    if (nextName !== curName) patch.name = nextName
    if (nextPhone !== curPhone) patch.phone = nextPhone
    if (nextEmail !== curEmail) patch.email = nextEmail
    if (nextType !== curType) patch.type = nextType
    if (nextActive !== curActive) patch.is_active = nextActive

    return patch
  }

  const onSave = async () => {
    if (!openCustomer) return
    setSaving(true)
    setSaveErr('')

    try {
      const patch = buildPatch()
      if (!Object.keys(patch).length) {
        setSaveErr('Aucune modification détectée.')
        setSaving(false)
        return
      }

      const updated = await apiFetch(`/api/customers/${openCustomer.uid}`, {
        method: 'PUT',
        json: patch,
      })

      setEditMode(false)
      onCustomerUpdated?.(updated)
    } catch (e) {
      setSaveErr(e?.message || 'Erreur mise à jour client')
    } finally {
      setSaving(false)
    }
  }

  if (!customers.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
        <div className="text-4xl mb-4">👥</div>
        <div className="mt-2 font-extrabold text-slate-900">Aucun client trouvé</div>
        <div className="mt-1 text-sm text-slate-600">Essaie une autre recherche ou enlève les filtres.</div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
              Nom
            </th>
            <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
              Téléphone
            </th>
            <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
              Commandes
            </th>
            <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
              Échéance
            </th>
            <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
              Fidélité
            </th>
            <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
              État
            </th>
            <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {customers.map((c) => {
            const isOpen = openId === c.uid
            const phoneEmpty = !c.phone || String(c.phone).trim() === ''

            return (
              <React.Fragment key={c.uid}>
                <tr className={cx('transition-colors', isOpen ? 'bg-slate-50' : 'hover:bg-slate-50')}>
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900">{c.name || '—'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-600 flex items-center gap-2">
                      <Phone className="h-3 w-3" />
                      {c.phone || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-600 flex items-center gap-2">
                      {c.email && <Mail className="h-3 w-3" />}
                      {c.email || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Pill tone="indigo">{c.type || 'particulier'}</Pill>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-semibold text-slate-900">{c.total_orders ?? 0}</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {c.has_deferred_payment && c.next_payment_due_at ? (
                      <div className="space-y-1">
                        <Pill tone={dueTone(c.next_payment_due_at)}>
                          <Bell className="h-3 w-3" />
                          {new Date(c.next_payment_due_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                        </Pill>
                        {Number(c.remaining_total || 0) > 0 && (
                          <div className="text-xs font-semibold text-slate-900">
                            {Number(c.remaining_total).toLocaleString('fr-FR')} FCFA
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400">—</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {rfmMap[c.uid]?.segment ? (() => {
                      const seg = RFM_SEG[rfmMap[c.uid].segment] || RFM_SEG.occasional
                      return (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${seg.cls}`}>
                          {seg.emoji} {seg.label}
                        </span>
                      )
                    })() : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Pill tone={c.is_active ? 'green' : 'red'}>{c.is_active ? 'Actif' : 'Inactif'}</Pill>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setOpenId(isOpen ? null : c.uid)}
                        disabled={saving}
                        className={cx(
                          'px-3 py-1 rounded-lg text-xs font-bold border transition',
                          saving
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : isOpen
                            ? 'bg-slate-900 border-slate-900 text-white hover:bg-slate-950'
                            : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-900'
                        )}
                      >
                        {isOpen ? 'Fermer' : 'Gérer'}
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Panneau d'édition expandable */}
                {isOpen && openCustomer && (
                  <tr className="bg-slate-50">
                    <td colSpan="8" className="px-6 py-6">
                      <div className="space-y-4">
                        {saveErr && (
                          <div className="p-3 border border-rose-200 rounded-xl bg-rose-50 text-rose-700 text-sm font-semibold">
                            {saveErr}
                          </div>
                        )}

                        {/* Badges info */}
                        <div className="flex gap-2 flex-wrap">
                          {c.has_deferred_payment && c.next_payment_due_at && (
                            <Pill tone={dueTone(c.next_payment_due_at)}>
                              <Bell className="h-3 w-3" />
                              {new Date(c.next_payment_due_at).toLocaleDateString('fr-FR')}
                            </Pill>
                          )}
                          {c.has_deferred_payment && Number(c.remaining_total || 0) > 0 && (
                            <Pill tone="blue">
                              Reste: {Number(c.remaining_total).toLocaleString('fr-FR')} FCFA
                            </Pill>
                          )}
                          {phoneEmpty && (
                            <Pill tone="orange">
                              <AlertCircle className="h-3 w-3" />
                              Tél. manquant
                            </Pill>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          {!editMode ? (
                            <button
                              onClick={() => setEditMode(true)}
                              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                            >
                              Modifier
                            </button>
                          ) : (
                            <>
                              <button
                                disabled={saving}
                                onClick={() => setEditMode(false)}
                                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
                              >
                                Annuler
                              </button>
                              <button
                                disabled={saving}
                                onClick={onSave}
                                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                {saving ? 'Enregistrement...' : 'Sauvegarder'}
                              </button>
                            </>
                          )}
                        </div>

                        {/* Formulaire d'édition */}
                        {editMode && (
                          <div className="rounded-xl border border-slate-200 bg-white p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <div className="text-xs font-semibold text-slate-500 mb-1">Nom *</div>
                                <input
                                  type="text"
                                  value={form.name}
                                  onChange={onChange('name')}
                                  className={inputBase}
                                  placeholder="Nom du client"
                                />
                              </div>

                              <div>
                                <div className="text-xs font-semibold text-slate-500 mb-1">Téléphone</div>
                                <input
                                  type="tel"
                                  value={form.phone}
                                  onChange={onChange('phone')}
                                  className={inputBase}
                                  placeholder="+225..."
                                />
                              </div>

                              <div>
                                <div className="text-xs font-semibold text-slate-500 mb-1">Email</div>
                                <input
                                  type="email"
                                  value={form.email}
                                  onChange={onChange('email')}
                                  className={inputBase}
                                  placeholder="email@exemple.com"
                                />
                              </div>

                              <div>
                                <div className="text-xs font-semibold text-slate-500 mb-1">Type</div>
                                <select value={form.type} onChange={onChange('type')} className={inputBase}>
                                  <option value="particulier">Particulier</option>
                                  <option value="professionnel">Professionnel</option>
                                  <option value="entreprise">Entreprise</option>
                                </select>
                              </div>

                              <div className="md:col-span-2">
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={onChange('is_active')}
                                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                                  />
                                  <span className="text-sm font-medium text-slate-900">Client actif</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Commandes récentes */}
                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                          <div className="text-sm font-extrabold text-slate-900 mb-3">
                            Commandes récentes ({orders.length})
                          </div>
                          {loadingOrders ? (
                            <div className="text-sm text-slate-500">Chargement...</div>
                          ) : orders.length === 0 ? (
                            <div className="text-sm text-slate-500">Aucune commande</div>
                          ) : (
                            <div className="space-y-2">
                              {orders.slice(0, 5).map((o, idx) => (
                                <div
                                  key={idx}
                                  className="flex justify-between py-2 border-b border-slate-100 last:border-0"
                                >
                                  <div className="text-sm font-medium text-slate-900">
                                    {o.external_id || o.id}
                                  </div>
                                  <div className="text-sm text-slate-600">
                                    {Number(o.total_ttc || 0).toLocaleString('fr-FR')} FCFA
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
